import { apiClient } from '../config/api'
import { Vehicle, VehicleHistory, FilterOptions, VehicleStatus } from '../types'

class VehicleService {
  async getVehicles(_filters?: FilterOptions): Promise<Vehicle[]> {
    try {
      // Get vehicles from /api/vehicles endpoint which includes assets and containers with balise assignments
      const response = await apiClient.get('/vehicles')
      
      // Handle the response which contains both assets and containers
      const vehiclesData = response.data || []
      
      // Transform vehicle data (assets/containers with balise info) to Vehicle format
      return vehiclesData.map((vehicleData: any) => this.transformVehicleDataToVehicle(vehicleData))
    } catch (error) {
      console.error('Error fetching vehicles:', error)
      throw error
    }
  }

  async getVehicle(id: string): Promise<Vehicle | null> {
    try {
      const response = await apiClient.get(`/balises/${id}`)
      return this.transformBaliseToVehicle(response.data)
    } catch (error) {
      console.error('Error fetching vehicle:', error)
      return null
    }
  }

  async getVehicleHistory(vehicleId: string, startDate?: Date, endDate?: Date): Promise<VehicleHistory[]> {
    try {
      // Calculate hours from date range or default to 7 days
      const defaultEndDate = endDate || new Date()
      const defaultStartDate = startDate || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) // 7 days ago
      const hours = Math.ceil((defaultEndDate.getTime() - defaultStartDate.getTime()) / (1000 * 60 * 60))

      // Use the new timeline endpoint that returns data in the correct format
      const response = await apiClient.get(`/timeline/${vehicleId}`, { hours })
      const historyData = response.data

      // Data is already in the correct VehicleHistory format from the API
      return historyData.map((point: any) => ({
        vehicleId: point.vehicleId,
        timestamp: new Date(point.timestamp),
        location: {
          latitude: point.location.latitude,
          longitude: point.location.longitude,
          accuracy: point.location.accuracy || 5
        },
        speed: point.speed || 0,
        heading: point.heading || 0,
        event: point.event || '',
        batteryLevel: point.batteryLevel,
        signalStrength: point.signalStrength
      }))
    } catch (error) {
      console.error('Error fetching vehicle history:', error)
      return []
    }
  }

  private transformBaliseToVehicle(balise: any): Vehicle {
    return {
      id: balise.universalId || balise.lockCode || balise.id,
      name: balise.deviceName || balise.name || `Vehicle ${balise.lockCode}`,
      plateNumber: balise.lockCode || balise.plateNumber || 'N/A',
      status: this.determineVehicleStatus(balise),
      location: {
        latitude: balise.location?.latitude || 0,
        longitude: balise.location?.longitude || 0,
        address: balise.location?.address || '',
        accuracy: balise.location?.accuracy || 0
      },
      speed: balise.speed || 0,
      heading: balise.heading || 0,
      lastUpdate: new Date(balise.updatedAt || balise.lastUpdate || balise.createdAt || Date.now()),
      batteryLevel: balise.batteryLevel,
      signalStrength: balise.signalStrength,
      driver: balise.assignedCustomer ? {
        id: balise.customerId?.toString() || '1',
        name: balise.assignedCustomer || 'Unknown Driver',
        phone: balise.customerPhone || ''
      } : undefined,
      fuel: balise.fuelLevel,
      temperature: balise.deviceTemperature,
      odometer: balise.odometer,
      engineHours: balise.engineHours
    }
  }

  private transformVehicleDataToVehicle(vehicleData: any): Vehicle {
    const isAsset = vehicleData.vehicle_source === 'ASSET'
    const vehicleId = `${vehicleData.vehicle_source}-${vehicleData.vehicle_id}`
    const plate = isAsset ? String(vehicleData.license_plate || '').trim() : ''
    const equipmentReference = String(vehicleData.equipment_reference || vehicleData.vehicle_code || '').trim()
    const brand = String(vehicleData.brand || vehicleData.make || '').trim()
    const model = String(vehicleData.model || '').trim()
    const customName = String(vehicleData.custom_name || '').trim()
    const vehicleCode = String(vehicleData.vehicle_code || '').trim()
    const operationalIdentifier = isAsset ? (plate || vehicleCode) : equipmentReference
    const displayName = isAsset
      ? (customName || [brand, model].filter(Boolean).join(' ') || plate || vehicleCode || 'Unknown asset')
      : (customName || equipmentReference || 'Unknown container')
    
    // Last known position from the assigned balise (synced into orchestration_balises)
    const location = {
      latitude: Number(vehicleData.last_latitude) || 0,
      longitude: Number(vehicleData.last_longitude) || 0,
      address: vehicleData.current_location || '',
      accuracy: 5
    }

    return {
      id: vehicleId,
      name: displayName,
      vehicleCode,
      customName: customName || undefined,
      plateNumber: plate,
      operationalIdentifier,
      iconKey: vehicleData.icon_key || undefined,
      identifierType: isAsset ? (plate ? 'License Plate' : 'Asset Code') : 'Container Number',
      status: this.mapTrackingToVehicleStatus(vehicleData),
      location: location,
      speed: Number(vehicleData.current_speed) || 0,
      currentSpeed: vehicleData.current_speed == null ? null : Number(vehicleData.current_speed),
      heading: 0, // Would need real-time data
      lastUpdate: new Date(vehicleData.balise_last_seen || vehicleData.created_at || Date.now()),
      trackingStatus: vehicleData.tracking_status || 'NOT_TRACKED',
      vehicleSource: isAsset ? 'ASSET' : 'CONTAINER',
      baliseCodes: String(vehicleData.balise_codes || '')
        .split(',')
        .map((code: string) => code.trim())
        .filter(Boolean),
      baliseBattery: vehicleData.balise_battery == null ? null : Number(vehicleData.balise_battery),
      baliseCount: Number(vehicleData.balise_count || 0),
      batteryLevel: vehicleData.balise_battery == null ? undefined : Number(vehicleData.balise_battery),
      // Include asset or container info
      assetInfo: isAsset ? {
        id: vehicleData.vehicle_id,
        code: vehicleData.vehicle_code,
        type: vehicleData.asset_type || vehicleData.vehicle_type,
        brand,
        model: vehicleData.model,
        licensePlate: plate || undefined,
        status: vehicleData.vehicle_status,
        customerName: vehicleData.customer_name,
        customerCode: vehicleData.customer_code
      } : undefined,
      containerInfo: !isAsset ? {
        id: vehicleData.vehicle_id,
        name: equipmentReference,
        equipmentReference,
        type: vehicleData.container_type || vehicleData.vehicle_type,
        owner: vehicleData.container_owner,
        origin: vehicleData.container_origin,
        destination: vehicleData.container_destination,
        bookingReference: vehicleData.booking_reference,
        status: vehicleData.vehicle_status,
        customerName: vehicleData.customer_name,
        customerCode: vehicleData.customer_code
      } : undefined,
      customerInfo: vehicleData.customer_name ? {
        id: vehicleData.customer_id,
        name: vehicleData.customer_name,
        code: vehicleData.customer_code,
        source: isAsset ? 'asset' : 'container'
      } : undefined,
      transport: (vehicleData.transport_corridor_id || vehicleData.departure_name || vehicleData.destination_name) ? {
        corridorId: vehicleData.transport_corridor_id || null,
        corridorName: vehicleData.transport_corridor_name || null,
        departure: vehicleData.departure_name || null,
        destination: vehicleData.destination_name || null,
        progress: vehicleData.transport_progress == null ? null : Number(vehicleData.transport_progress),
        source: vehicleData.transport_source || null
      } : null
    }
  }

  /** Places for departure/destination pickers (trajectory endpoints + geofences) */
  async getPlaces(): Promise<Array<{ name: string; lat: number; lng: number; origin: string }>> {
    const response = await apiClient.get('/places')
    return response.data || []
  }

  /** Assign a transport (itinerary or departure/destination) to a vehicle */
  async setTransport(
    vehicleSource: 'ASSET' | 'CONTAINER',
    vehicleId: number,
    payload:
      | { corridor_id: string }
      | { departure: { name: string; lat: number; lng: number }, destination: { name: string; lat: number; lng: number } }
      | { clear: true }
  ): Promise<void> {
    await apiClient.put(`/vehicles/${vehicleSource}/${vehicleId}/transport`, payload)
  }

  /**
   * Honest status: derived from balise telemetry (tracking_status computed
   * server-side), never from the admin status column. A vehicle with no
   * balise can NEVER be "moving" — it is offline (untracked) by definition.
   */
  private mapTrackingToVehicleStatus(vehicleData: any): VehicleStatus {
    const adminStatus = String(vehicleData.vehicle_status || '').toUpperCase()
    if (adminStatus.includes('MAINTENANCE') || adminStatus.includes('REPAIR')) {
      return VehicleStatus.MAINTENANCE
    }

    switch (vehicleData.tracking_status) {
      case 'LIVE':
        // Balise reporting recently; without live speed we show idle, not moving
        return VehicleStatus.IDLE
      case 'STALE':
        return VehicleStatus.STOPPED
      case 'OFFLINE':
        return VehicleStatus.OFFLINE
      case 'NOT_TRACKED':
      default:
        return VehicleStatus.OFFLINE
    }
  }

  private determineVehicleStatus(balise: any): VehicleStatus {
    const lastUpdate = new Date(balise.updatedAt || balise.lastUpdate || balise.createdAt || 0)
    const now = new Date()
    const minutesSinceUpdate = (now.getTime() - lastUpdate.getTime()) / (1000 * 60)

    // If no update in 30 minutes, consider offline
    if (minutesSinceUpdate > 30) {
      return VehicleStatus.OFFLINE
    }

    // Check operational status
    if (balise.operationalStatus === 'MAINTENANCE' || balise.healthStatus === 'MAINTENANCE') {
      return VehicleStatus.MAINTENANCE
    }

    // If speed > 5 km/h, consider moving
    if (balise.speed > 5) {
      return VehicleStatus.MOVING
    }

    // If speed between 0-5 km/h, consider idle
    if (balise.speed > 0) {
      return VehicleStatus.IDLE
    }

    // Default to stopped
    return VehicleStatus.STOPPED
  }

}

export const vehicleService = new VehicleService()
